import React, { useEffect, useMemo, useState } from "react";
import SelectBox from "devextreme-react/select-box";
import { Button } from "devextreme-react/button";
import DateBox from "devextreme-react/date-box";
import DataGrid, { Column, Editing } from "devextreme-react/data-grid";
import "./PreTradeVar.css";
import { baseUrl } from "../../hooks/useFetch";
import { fmtISO } from "../../utils/dateUtils";

const MAJOR_CCY: string[] = ["USD", "EUR", "GBP", "CHF", "JPY", "CAD", "AUD", "NZD", "SEK", "NOK", "DKK"];

interface Trade {
  id: number;
  productType: string;
  tradeName: string;
  book: string;
  underlying: string;
  valuationDate: Date;
  period: string;
  endDate: string;
  delta?: number;
  price?: number;
  currency?: string;
  notional?: number;
  fx?: number;
  notionalEUR?: number;
  callPut?: string;
  strike?: number;
  vol?: string;
  rate?: string;
}

interface BucketInfo {
  bucket: string;
  var_eur: number;
  books: string[];
  underlyings: string[];
}

interface VarBucketRow {
  Bucket: string;
  "VaR (EUR)"?: number;
  "Current VaR (EUR)"?: number;
  "Proposed VaR (EUR)"?: number;
  "VaR Change (EUR)"?: number;
  "VaR Change (%)"?: number;
  Books: string;
}

interface VarResponse {
  var_by_bucket: VarBucketRow[];
  book_allocation: any[];
  key_allocation: any[];
  fallback_used?: boolean;
  fallback_reason?: string;
}

interface ResultRow {
  tradeType?: string;
  bucket: string;
  books: string;
  currentVarEur: number;
  proposedVarEur: number;
  changeEur: number;
  changePct: number;
  measure: string;
}

interface CalcResults {
  linear: VarResponse | null;
  nonLinear: VarResponse | null;
  combined: VarResponse | null;
}


const VAR_MODES = ["Marginal VaR", "Marginal + Fallback", "Full VaR"];
const MODE_MAP: Record<string, string> = {
  "Marginal VaR": "marginal",
  "Marginal + Fallback": "marginal_fallback",
  "Full VaR": "full",
};

let nextLinearId = 1;
let nextOptionId = 1000;

const PreTradeVar: React.FC = () => {
  const [riskMeasure, setRiskMeasure] = useState<string>("1 day VaR (95%)");
  const [varMode, setVarMode] = useState<string>("Marginal VaR");
  const [cobDate, setCobDate] = useState<Date>(new Date(2026, 0, 21));

  const [buckets, setBuckets] = useState<BucketInfo[]>([]);
  const [selectedBucket, setSelectedBucket] = useState<string>("Structured Carbon");

  const [linearTrades, setLinearTrades] = useState<Trade[]>([]);
  const [optionTrades, setOptionTrades] = useState<Trade[]>([]);

  const [calcResults, setCalcResults] = useState<CalcResults | null>(null);
  const [loadingInit, setLoadingInit] = useState<boolean>(false);
  const [loadingRun, setLoadingRun] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string>("");

  const isFullVar = varMode === "Full VaR";
  const isMarginal = varMode === "Marginal VaR";
  const showFullFields = !isMarginal && !isFullVar;

  const bucketNames = useMemo(() => buckets.map((b) => b.bucket), [buckets]);

  const bookOptions = useMemo(() => {
    const found = buckets.find((b) => b.bucket === selectedBucket);
    return found?.books || [];
  }, [buckets, selectedBucket]);

  const underlyingOptions = useMemo(() => {
    const found = buckets.find((b) => b.bucket === selectedBucket);
    return found?.underlyings || [];
  }, [buckets, selectedBucket]);

  // Force DataGrid remount when lookup data changes to avoid DevExtreme calculateCellValue error
  const gridKey = useMemo(() => `${selectedBucket}-${bookOptions.length}-${underlyingOptions.length}`, [selectedBucket, bookOptions.length, underlyingOptions.length]);

  // Auto-fill book on bucket change
  useEffect(() => {
    const found = buckets.find((b) => b.bucket === selectedBucket);
    const defaultBook = found?.books?.[0] || "";
    if (!defaultBook) return;
    setLinearTrades((prev) => prev.map((t) => (t.book ? t : { ...t, book: defaultBook })));
    setOptionTrades((prev) => prev.map((t) => (t.book ? t : { ...t, book: defaultBook })));
  }, [selectedBucket, buckets]);

  async function loadInit(): Promise<void> {
    setLoadingInit(true);
    setErrorMsg("");
    try {
      const cobStr = fmtISO(cobDate) ?? "";
      const bRes = await fetch(`${baseUrl}/analytics/var_buckets?cob_date=${encodeURIComponent(cobStr)}`);
      if (!bRes.ok) throw new Error(`var_buckets api failed (${bRes.status})`);
      const bJson: BucketInfo[] = await bRes.json();
      setBuckets(bJson);
      if (!selectedBucket && bJson.length) setSelectedBucket(bJson[0].bucket);
    } catch (e: any) {
      setErrorMsg(e?.message || "Failed to load initial data");
    } finally {
      setLoadingInit(false);
    }
  }

  useEffect(() => { loadInit(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    (async () => {
      try {
        const cobStr = fmtISO(cobDate) ?? "";
        const bRes = await fetch(`${baseUrl}/analytics/var_buckets?cob_date=${encodeURIComponent(cobStr)}`);
        if (!bRes.ok) return;
        const bJson: BucketInfo[] = await bRes.json();
        setBuckets(bJson);
      } catch { /* */ }
    })();
  }, [cobDate]);

  const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const MONTH_MAP: Record<string, number> = {};
  MONTHS.forEach((m, i) => { MONTH_MAP[m.toLowerCase()] = i; });

  function periodToEndDate(period: string): string | null {
    const match = period.match(/^([A-Za-z]{3})-(\d{2})$/);
    if (!match) return null;
    const monthIdx = MONTH_MAP[match[1].toLowerCase()];
    if (monthIdx === undefined) return null;
    const year = 2000 + parseInt(match[2], 10);
    const lastDay = new Date(year, monthIdx + 1, 0).getDate();
    return `${year}-${String(monthIdx + 1).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
  }

  function endDateToPeriod(endDate: string): string | null {
    const d = new Date(endDate);
    if (isNaN(d.getTime())) return null;
    return `${MONTHS[d.getMonth()]}-${String(d.getFullYear()).slice(2)}`;
  }

  function syncTrade(trade: Trade, changedField: string): Trade {
    if (changedField === "period" && trade.period) {
      const derived = periodToEndDate(trade.period);
      if (derived) return { ...trade, endDate: derived };
    }
    if (changedField === "endDate" && trade.endDate) {
      const derived = endDateToPeriod(trade.endDate);
      if (derived) return { ...trade, period: derived };
    }
    return trade;
  }

  function parseRiskMeasure(rm: string): { confidence: number; holdingPeriod: number } {
    if (rm.includes("99")) return { confidence: 99, holdingPeriod: 1 };
    return { confidence: 95, holdingPeriod: 1 };
  }

  function derivePeriod(t: Trade): string {
    if (t.endDate) {
      const d = new Date(t.endDate);
      if (!isNaN(d.getTime())) {
        const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
        return `${months[d.getMonth()]}-${String(d.getFullYear()).slice(2)}`;
      }
    }
    return String(t.period ?? "").trim();
  }

  function mapTrade(t: Trade) {
    return {
      product_type: t.productType || null,
      trade_name: t.tradeName || null,
      book: (t.book || "").trim() || null,
      underlying: t.underlying || null,
      valuation_date: t.valuationDate ? fmtISO(t.valuationDate) : null,
      period: derivePeriod(t) || null,
      end_date: t.endDate || null,
      delta: t.delta ?? null,
      price: t.price ?? null,
      currency: t.currency || null,
      notional: t.notional ?? null,
      fx: t.fx ?? null,
      notional_eur: t.notionalEUR ?? null,
      call_put: t.callPut || null,
      strike: t.strike ?? null,
      vol: t.vol || null,
      rate: t.rate || null,
    };
  }

  async function postVar(trades: any[]): Promise<VarResponse> {
    const cob_date = fmtISO(cobDate) ?? "";
    const modeKey = MODE_MAP[varMode] || "marginal";
    const { confidence, holdingPeriod } = parseRiskMeasure(riskMeasure);
    const res = await fetch(`${baseUrl}/analytics/calculate_var`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        cob_date,
        bucket: selectedBucket || null,
        confidence,
        holding_period: holdingPeriod,
        mode: modeKey,
        trades,
      }),
    });
    if (!res.ok) {
      const errBody = await res.text();
      throw new Error(`calculate_var failed (${res.status}): ${errBody}`);
    }
    return res.json();
  }

  async function runCalc(): Promise<void> {
    setErrorMsg("");
    setCalcResults(null);

    const cob_date = fmtISO(cobDate);
    if (!cob_date) { setErrorMsg("Missing CoB date"); return; }

    if (!isFullVar) {
      // Validate linear trades
      const invalidLinear = linearTrades.filter(
        (t) => !t.book?.trim() || !t.underlying?.trim() || t.delta == null
      );
      if (invalidLinear.length) {
        const names = invalidLinear.map((t) => `Linear #${t.id}`).join(", ");
        setErrorMsg(`Linear trades missing book, underlying, or delta: ${names}`);
        return;
      }

      // Validate option trades
      const invalidOption = optionTrades.filter(
        (t) => !t.book?.trim() || !t.underlying?.trim() || !t.callPut || t.strike == null || !t.vol || !t.rate || t.price == null || t.notionalEUR == null
      );
      if (invalidOption.length) {
        const names = invalidOption.map((t) => t.tradeName || `Option #${t.id}`).join(", ");
        setErrorMsg(`Option trades missing required fields: ${names}`);
        return;
      }

      if (!linearTrades.length && !optionTrades.length) {
        setErrorMsg("Add at least one trade");
        return;
      }
    }

    const linMapped = linearTrades.map((t) => mapTrade({ ...t, productType: "Linear" }));
    const optMapped = optionTrades.map((t) => mapTrade({ ...t, productType: "Option" }));
    const hasLinear = linMapped.length > 0;
    const hasOption = optMapped.length > 0;

    setLoadingRun(true);
    try {
      if (hasLinear && hasOption) {
        // Both types — 3 parallel calls
        const [linear, nonLinear, combined] = await Promise.all([
          postVar(linMapped),
          postVar(optMapped),
          postVar([...linMapped, ...optMapped]),
        ]);
        setCalcResults({ linear, nonLinear, combined });
      } else if (hasLinear) {
        const linear = await postVar(linMapped);
        setCalcResults({ linear, nonLinear: null, combined: null });
      } else if (hasOption) {
        const nonLinear = await postVar(optMapped);
        setCalcResults({ linear: null, nonLinear, combined: null });
      } else if (isFullVar) {
        const combined = await postVar([]);
        setCalcResults({ linear: null, nonLinear: null, combined });
      }
    } catch (e: any) {
      setErrorMsg(e?.message || "RUN failed");
    } finally {
      setLoadingRun(false);
    }
  }

  const isMixedResult = !!(calcResults?.linear && calcResults?.nonLinear);

  function varResponseToRow(resp: VarResponse | null, tradeType?: string): ResultRow | null {
    const b = resp?.var_by_bucket?.[0];
    if (!b) return null;
    const { confidence, holdingPeriod } = parseRiskMeasure(riskMeasure);
    return {
      tradeType,
      bucket: b.Bucket,
      books: b.Books,
      currentVarEur: b["Current VaR (EUR)"] ?? b["VaR (EUR)"] ?? 0,
      proposedVarEur: b["Proposed VaR (EUR)"] ?? b["VaR (EUR)"] ?? 0,
      changeEur: b["VaR Change (EUR)"] ?? 0,
      changePct: b["VaR Change (%)"] ?? 0,
      measure: `${holdingPeriod} day VaR (${confidence}%)`,
    };
  }

  const resultsRows = useMemo((): ResultRow[] => {
    if (!calcResults) return [];
    const rows: ResultRow[] = [];

    if (isMixedResult) {
      // Both trade types — show 3 rows with Trade Type column
      const lin = varResponseToRow(calcResults.linear, "Linear");
      const opt = varResponseToRow(calcResults.nonLinear, "Non-Linear");
      const tot = varResponseToRow(calcResults.combined, "Total");
      if (lin) rows.push(lin);
      if (opt) rows.push(opt);
      if (tot) rows.push(tot);
    } else {
      // Single trade type — one row, no Trade Type label
      const single = varResponseToRow(
        calcResults.linear || calcResults.nonLinear || calcResults.combined
      );
      if (single) rows.push(single);
    }

    return rows;
  }, [calcResults, riskMeasure]);

  return (
    <div className="risk-var-container">
      <h2 className="var-title">Pre-Trade Analysis Tool</h2>

      <div className="controls-row">
        <div className="control-cell">
          <label>CoB:</label>
          <DateBox value={cobDate} type="date" displayFormat="dd/MM/yyyy" onValueChanged={(e: any) => setCobDate(e.value)} className="date-picker" />
        </div>
        <div className="control-cell">
          <label>1. Risk measure</label>
          <SelectBox dataSource={["1 day VaR (95%)", "1 day VaR (99%)"]} value={riskMeasure} onValueChanged={(e: any) => setRiskMeasure(e.value)} className="dropdown" />
        </div>
        <div className="control-cell">
          <label>2. Mode</label>
          <SelectBox dataSource={VAR_MODES} value={varMode} onValueChanged={(e: any) => setVarMode(e.value)} className="dropdown" />
        </div>
        <div className="control-cell">
          <label>3. Portfolio (bucket)</label>
          <SelectBox dataSource={bucketNames} value={selectedBucket} onValueChanged={(e: any) => setSelectedBucket(e.value)} searchEnabled={true} className="dropdown" placeholder="Select a bucket..." />
        </div>
      </div>

      {/* ---- Trade Grids (hidden in Full VaR mode) ---- */}
      {isFullVar && (
        <div className="section-var" style={{ padding: "12px 0", color: "#666", fontStyle: "italic" }}>
          Full VaR runs the entire portfolio — no individual trades needed.
        </div>
      )}

      {!isFullVar && <>
      {/* ---- Linear Trades Grid ---- */}
      <div className="section-var section-trade">
        <label className="with-spacing">4a. Linear Trades</label>
        <DataGrid
          key={`linear-${gridKey}`}
          dataSource={linearTrades}
          keyExpr="id"
          showBorders={true}
          showColumnLines={true}
          columnAutoWidth={true}
          rowAlternationEnabled={true}
          className="trade-grid"
          onRowInserted={(e: any) => {
            e.data.id = nextLinearId++;
            e.data.productType = "Linear";
            const items: Trade[] = e.component.getDataSource().items();
            setLinearTrades(items.map((t) => {
              if (t.id === e.data.id) {
                let synced = t;
                if (t.period) synced = syncTrade(synced, "period");
                else if (t.endDate) synced = syncTrade(synced, "endDate");
                return synced;
              }
              return t;
            }));
          }}
          onRowUpdated={(e: any) => {
            const changedFields = Object.keys(e.data);
            setLinearTrades((prev) => prev.map((t) => {
              if (t.id !== e.key) return t;
              let synced = { ...t };
              if (changedFields.includes("period")) synced = syncTrade(synced, "period");
              else if (changedFields.includes("endDate")) synced = syncTrade(synced, "endDate");
              return synced;
            }));
          }}
          onRowRemoved={(e: any) => { setLinearTrades(e.component.getDataSource().items().filter((x: any) => x.id !== e.key)); }}
        >
          <Editing mode="row" allowUpdating={true} allowDeleting={true} allowAdding={true} />
          <Column dataField="book" caption="Book" minWidth={200} lookup={{ dataSource: bookOptions, valueExpr: "this", displayExpr: "this" }} />
          <Column dataField="underlying" caption="Underlying" minWidth={140} lookup={{ dataSource: underlyingOptions, valueExpr: "this", displayExpr: "this" }} />
          <Column dataField="period" caption="Period" dataType="string" editorOptions={{ placeholder: "e.g. Dec-26" }} setCellValue={(newData: any, value: string) => { const v = value ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase() : value; newData.period = v; const ed = periodToEndDate(v); if (ed) newData.endDate = ed; }} />
          <Column dataField="delta" caption="Delta" dataType="number" />

          {/* Extra columns in Full / Fallback modes */}
          <Column dataField="endDate" caption="End Date" visible={showFullFields} editorOptions={{ placeholder: "YYYY-MM-DD" }} setCellValue={(newData: any, value: string) => { newData.endDate = value; const p = endDateToPeriod(value); if (p) newData.period = p; }} />
          <Column dataField="price" caption="Price" dataType="number" visible={showFullFields} />
          <Column dataField="currency" caption="Currency" visible={showFullFields} lookup={{ dataSource: MAJOR_CCY, valueExpr: "this", displayExpr: "this" }} />
          <Column dataField="notional" caption="Notional" dataType="number" visible={showFullFields} />
          <Column dataField="fx" caption="FX" dataType="number" visible={showFullFields} />
          <Column dataField="notionalEUR" caption="Notional (EUR)" dataType="number" visible={showFullFields} />
        </DataGrid>
      </div>

      {/* ---- Option Trades Grid ---- */}
      <div className="section-var section-trade">
        <label className="with-spacing">4b. Option Trades</label>
        <DataGrid
          key={`option-${gridKey}`}
          dataSource={optionTrades}
          keyExpr="id"
          showBorders={true}
          showColumnLines={true}
          columnAutoWidth={true}
          rowAlternationEnabled={true}
          className="trade-grid"
          onInitNewRow={(e: any) => { e.data.valuationDate = cobDate; }}
          onRowInserted={(e: any) => {
            e.data.id = nextOptionId++;
            e.data.productType = "Option";
            const items: Trade[] = e.component.getDataSource().items();
            setOptionTrades(items.map((t) => {
              if (t.id === e.data.id) {
                let synced = t;
                if (t.period) synced = syncTrade(synced, "period");
                else if (t.endDate) synced = syncTrade(synced, "endDate");
                return synced;
              }
              return t;
            }));
          }}
          onRowUpdated={(e: any) => {
            const changedFields = Object.keys(e.data);
            setOptionTrades((prev) => prev.map((t) => {
              if (t.id !== e.key) return t;
              let synced = { ...t };
              if (changedFields.includes("period")) synced = syncTrade(synced, "period");
              else if (changedFields.includes("endDate")) synced = syncTrade(synced, "endDate");
              return synced;
            }));
          }}
          onRowRemoved={(e: any) => { setOptionTrades(e.component.getDataSource().items().filter((x: any) => x.id !== e.key)); }}
        >
          <Editing mode="row" allowUpdating={true} allowDeleting={true} allowAdding={true} />
          <Column dataField="book" caption="Book" minWidth={200} lookup={{ dataSource: bookOptions, valueExpr: "this", displayExpr: "this" }} />
          <Column dataField="underlying" caption="Underlying" minWidth={140} lookup={{ dataSource: underlyingOptions, valueExpr: "this", displayExpr: "this" }} />
          <Column dataField="period" caption="Period" dataType="string" editorOptions={{ placeholder: "e.g. Dec-26" }} setCellValue={(newData: any, value: string) => { const v = value ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase() : value; newData.period = v; const ed = periodToEndDate(v); if (ed) newData.endDate = ed; }} />
          <Column dataField="callPut" caption="Call/Put" lookup={{ dataSource: ["C", "P"], valueExpr: "this", displayExpr: "this", searchEnabled: true }} editorOptions={{ searchEnabled: true, searchMode: "contains", searchExpr: "this", acceptCustomValue: false, onInput: (e: any) => { const input = e.event?.target; if (input?.value) input.value = input.value.toUpperCase(); } }} setCellValue={(newData: any, value: any) => { newData.callPut = typeof value === "string" ? value.toUpperCase() : value; }} />
          <Column dataField="strike" caption="Strike" dataType="number" />
          <Column dataField="vol" caption="Vol (%)" dataType="string" editorOptions={{ placeholder: "e.g. 20" }} />
          <Column dataField="rate" caption="Rate (%)" dataType="string" editorOptions={{ placeholder: "e.g. 3" }} />
          <Column dataField="price" caption="Price" dataType="number" />
          <Column dataField="notionalEUR" caption="Notional (EUR)" dataType="number" format="#,##0" editorOptions={{ format: "#,##0" }} />

          {/* Dates always visible for options (needed for BS delta) */}
          <Column dataField="endDate" caption="End Date" editorOptions={{ placeholder: "YYYY-MM-DD" }} setCellValue={(newData: any, value: string) => { newData.endDate = value; const p = endDateToPeriod(value); if (p) newData.period = p; }} />
          <Column dataField="valuationDate" caption="Valuation Date" dataType="date" format="dd/MM/yyyy" />

          {/* Extra columns in Full / Fallback modes */}
          <Column dataField="tradeName" caption="Trade Name" visible={showFullFields} />
          <Column dataField="currency" caption="Currency" visible={showFullFields} lookup={{ dataSource: MAJOR_CCY, valueExpr: "this", displayExpr: "this" }} />
          <Column dataField="notional" caption="Notional" dataType="number" visible={showFullFields} />
          <Column dataField="fx" caption="FX" dataType="number" visible={showFullFields} />
        </DataGrid>
      </div>
      </>}

      {/* ---- RUN Button ---- */}
      <div className="section-var" style={{ display: "flex", gap: 10, alignItems: "center" }}>
        <Button text={loadingRun ? "Running..." : "RUN"} type="default" stylingMode="contained" disabled={loadingRun} onClick={runCalc} className="run-btn" />
        {errorMsg ? <div style={{ color: "crimson", fontSize: 16, fontWeight: 600 }}>{errorMsg}</div> : null}
      </div>

      {/* ---- Fallback Notice ---- */}
      {(calcResults?.linear?.fallback_used || calcResults?.nonLinear?.fallback_used || calcResults?.combined?.fallback_used) && (
        <div className="fallback-notice">
          Some products were not in the cached covariance matrix. Full VaR engine was used.
        </div>
      )}

      {/* ---- Results Grid ---- */}
      <div className="section-var">
        <label>5. Results — Pre-Trade Impact</label>
        <DataGrid dataSource={resultsRows} showBorders={true} showColumnLines={true} columnAutoWidth={true} className="results-grid" width="100%">
          {isMixedResult && <Column dataField="tradeType" caption="Trade Type" minWidth={120} />}
          <Column dataField="bucket" caption="Bucket" minWidth={200} />
          <Column dataField="books" caption="Books" minWidth={280} cssClass="books-cell" />
          <Column dataField="currentVarEur" caption="Current VaR (EUR)" dataType="number" format="#,##0.00" minWidth={180} />
          <Column dataField="proposedVarEur" caption="Proposed VaR (EUR)" dataType="number" format="#,##0.00" minWidth={180} />
          <Column dataField="changeEur" caption="Change (EUR)" dataType="number" format="#,##0.00" minWidth={160} cssClass="var-change-cell" />
          <Column dataField="changePct" caption="Change (%)" dataType="number" format="#,##0.00" minWidth={130} cssClass="var-change-cell" />
          <Column dataField="measure" caption="Measure" minWidth={170} />
        </DataGrid>
      </div>
    </div>
  );
};

export default PreTradeVar;
